import React from "react";
import LoadingSpinner from "./LoadingSpinner";

/**
 * LoadingOverlay component provides a full-screen or container-relative loading overlay
 * with customizable messages and spinner sizes
 */
const LoadingOverlay = ({
  isVisible = false,
  message = "Loading...",
  size = "large",
  fullScreen = false,
  backdrop = true,
  className = "",
  children,
  onCancel,
  cancelText = "Cancel",
  showProgress = false,
  progress = 0,
  progressText = "",
  timeout,
  onTimeout,
}) => {
  const [timeoutReached, setTimeoutReached] = React.useState(false);

  // Handle timeout
  React.useEffect(() => {
    if (!isVisible || !timeout) return;

    const timer = setTimeout(() => {
      setTimeoutReached(true);
      if (onTimeout) {
        onTimeout();
      }
    }, timeout);

    return () => {
      clearTimeout(timer);
      setTimeoutReached(false);
    };
  }, [isVisible, timeout, onTimeout]);

  if (!isVisible) {
    return children || null;
  }

  const overlayClasses = [
    "loading-overlay",
    fullScreen ? "loading-overlay-fullscreen" : "loading-overlay-container",
    backdrop ? "loading-overlay-backdrop" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <div className={overlayClasses}>
      <div className="loading-overlay-content">
        <LoadingSpinner size={size} />

        {message && <div className="loading-overlay-message">{message}</div>}

        {showProgress && (
          <div className="loading-overlay-progress">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
              />
            </div>
            {progressText && (
              <div className="progress-text">{progressText}</div>
            )}
          </div>
        )}

        {timeoutReached && (
          <div className="loading-overlay-timeout">
            <p>This is taking longer than expected...</p>
          </div>
        )}

        {(onCancel || timeoutReached) && (
          <div className="loading-overlay-actions">
            {onCancel && (
              <button
                onClick={onCancel}
                className="loading-overlay-cancel"
                type="button"
              >
                {cancelText}
              </button>
            )}
            {timeoutReached && onTimeout && (
              <button
                onClick={onTimeout}
                className="loading-overlay-retry"
                type="button"
              >
                Retry
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // If we have children, render them with the overlay on top
  if (children) {
    return (
      <div className="loading-overlay-wrapper">
        {children}
        {content}
      </div>
    );
  }

  return content;
};

/**
 * Higher-order component that wraps a component with LoadingOverlay
 */
export const withLoadingOverlay = (Component, defaultProps = {}) => {
  const WrappedComponent = ({ isLoading, loadingMessage, ...props }) => (
    <LoadingOverlay
      isVisible={isLoading}
      message={loadingMessage}
      {...defaultProps}
    >
      <Component {...props} />
    </LoadingOverlay>
  );

  WrappedComponent.displayName = `withLoadingOverlay(${Component.displayName || Component.name})`;

  return WrappedComponent;
};

/**
 * Hook for managing loading overlay state
 */
export const useLoadingOverlay = (initialState = false) => {
  const [isLoading, setIsLoading] = React.useState(initialState);
  const [message, setMessage] = React.useState("");
  const [progress, setProgress] = React.useState(0);

  const showLoading = React.useCallback((loadingMessage = "Loading...") => {
    setMessage(loadingMessage);
    setIsLoading(true);
    setProgress(0);
  }, []);

  const hideLoading = React.useCallback(() => {
    setIsLoading(false);
    setMessage("");
    setProgress(0);
  }, []);

  const updateProgress = React.useCallback(
    (newProgress, progressMessage = "") => {
      setProgress(newProgress);
      if (progressMessage) {
        setMessage(progressMessage);
      }
    },
    [],
  );

  const withLoading = React.useCallback(
    async (operation, loadingMessage = "Loading...") => {
      try {
        showLoading(loadingMessage);
        const result = await operation();
        return result;
      } finally {
        hideLoading();
      }
    },
    [showLoading, hideLoading],
  );

  return {
    isLoading,
    message,
    progress,
    showLoading,
    hideLoading,
    updateProgress,
    withLoading,
  };
};

export default LoadingOverlay;
